import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Heart, Search, ShoppingCart, X } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, IMAGES } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { api, asError } from "@/api/client";
import { normalizeImageUrl } from "@/lib/imageUrl";
import Reveal from "@/components/Reveal";

const MILLET_TYPES = ["Ragi", "Foxtail Millet", "Pearl Millet", "Little Millet", "Barnyard Millet"];
const HEALTH_PREFS = ["Gluten Free", "High Protein", "High Fibre", "No Maida", "Vegan"];
const SORT_OPTIONS = ["Popularity", "Price: Low to High", "Price: High to Low", "New Launch"];

const priceNum = (p) => typeof p === "number" ? p : parseInt(String(p).replace(/[^\d]/g, ""), 10) || 0;
const weightNum = (w) => parseFloat(String(w).replace(/[^\d.]/g, "")) || 0;

export default function Products() {
  const { add } = useCart();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Popularity");
  const [millet, setMillet] = useState([]);
  const [health, setHealth] = useState([]);
  const [priceMax, setPriceMax] = useState(500);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submittingEnquiry, setSubmittingEnquiry] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/products", { params: { limit: 24, page: 1 } })
      .then(({ data }) => {
        if (!alive) return;
        setAllProducts(data.items || []);
        setHasMore(!!data.has_more);
      })
      .catch(() => { if (alive) setAllProducts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/products", { params: { limit: 24, page: page + 1 } });
      setAllProducts((prev) => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
      setPage(page + 1);
    } finally { setLoadingMore(false); }
  };

  // Tag products with badges + per-100g (computed after fetch so new admin products participate)
  const enriched = useMemo(() => {
    return allProducts.map((p, idx) => {
      const w = weightNum(p.weight);
      return {
        ...p,
        image: normalizeImageUrl(p.image),
        badge: idx < 3 ? "BESTSELLER" : idx % 5 === 0 ? "NEW LAUNCH" : null,
        per100g: w > 0 ? `(₹${Math.round((priceNum(p.price) / w) * 100)}/100g)` : "",
      };
    });
  }, [allProducts]);

  const list = useMemo(() => {
    let arr = active === "all" ? enriched : enriched.filter((p) => p.category === active);
    if (search.trim()) arr = arr.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
    arr = arr.filter((p) => priceNum(p.price) <= priceMax);
    if (sort === "Price: Low to High") arr = [...arr].sort((a, b) => priceNum(a.price) - priceNum(b.price));
    if (sort === "Price: High to Low") arr = [...arr].sort((a, b) => priceNum(b.price) - priceNum(a.price));
    if (sort === "New Launch") arr = [...arr].sort((a, b) => (b.badge === "NEW LAUNCH" ? 1 : 0) - (a.badge === "NEW LAUNCH" ? 1 : 0));
    return arr;
  }, [enriched, active, search, sort, priceMax]);

  const onAdd = (p) => {
    add({ ...p, price: priceNum(p.price) }, 1);
    toast.success(`${p.name} added to cart.`);
  };

  const onWishlist = (p) => toast.info(`${p.name} saved to wishlist.`);

  const clearAll = () => {
    setActive("all"); setSearch(""); setMillet([]); setHealth([]); setPriceMax(500);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and mobile number are required."); return; }
    setSubmittingEnquiry(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message,
        product_id: open?.id,
        product_name: open?.name,
      });
      toast.success(`Enquiry sent for ${open?.name}. We'll respond within 24h.`);
      setOpen(null);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(asError(err));
    } finally {
      setSubmittingEnquiry(false);
    }
  };

  return (
    <div data-testid="page-products" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-8 md:pb-10 bg-[var(--bg)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.packaged} alt="" className="w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg)] via-[var(--bg)]/85 to-transparent" />
        </div>
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline overline-green">The Pantry</div>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              Shop All Products
            </h1>
            <p className="mt-4 text-base md:text-lg text-[var(--ink-2)] max-w-xl">
              Wholesome millet foods for every lifestyle and occasion.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CATEGORY PILLS */}
      <section className="border-y border-[var(--line-soft)] bg-white sticky top-[78px] z-30">
        <div className="max-w-[1480px] mx-auto px-4 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
          <span className="kbd text-[var(--ink-3)] mr-1 shrink-0">Filter</span>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`shrink-0 text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-full border transition ${
                active === c.id
                  ? "bg-[var(--green)] text-white border-[var(--green)]"
                  : "bg-transparent text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--green)] hover:text-[var(--green)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="py-10 md:py-14">
        <div className="max-w-[1480px] mx-auto px-4 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="card-soft p-5 sticky top-[155px]">
              <div className="flex justify-between items-center">
                <h3 className="font-serif-display text-xl font-semibold text-[var(--green)]">Filter Products</h3>
                <button onClick={clearAll} data-testid="filter-clear" className="text-xs text-[var(--green)] underline">Clear All</button>
              </div>

              <Group title="Millet Type">
                {MILLET_TYPES.map((m) => (
                  <CheckRow key={m} testId={`millet-${m.toLowerCase().replace(/\s+/g, "-")}`} label={m}
                    checked={millet.includes(m)} onChange={() => setMillet((v) => v.includes(m) ? v.filter(x => x !== m) : [...v, m])} />
                ))}
              </Group>

              <Group title="Category">
                {CATEGORIES.filter(c => c.id !== "all").map((c) => (
                  <CheckRow key={c.id} testId={`sidebar-cat-${c.id}`} label={c.label}
                    checked={active === c.id} onChange={() => setActive(active === c.id ? "all" : c.id)} />
                ))}
              </Group>

              <Group title="Health Preference">
                {HEALTH_PREFS.map((m) => (
                  <CheckRow key={m} testId={`health-${m.toLowerCase().replace(/\s+/g, "-")}`} label={m}
                    checked={health.includes(m)} onChange={() => setHealth((v) => v.includes(m) ? v.filter(x => x !== m) : [...v, m])} />
                ))}
              </Group>

              <Group title="Price Range">
                <input
                  data-testid="filter-price-slider"
                  type="range"
                  min="50"
                  max="500"
                  value={priceMax}
                  onChange={(e) => setPriceMax(parseInt(e.target.value))}
                  className="w-full accent-[var(--green)]"
                />
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span className="border border-[var(--line)] rounded px-3 py-1">₹0</span>
                  <span className="text-[var(--ink-3)]">to</span>
                  <span className="border border-[var(--line)] rounded px-3 py-1">₹{priceMax}</span>
                </div>
              </Group>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                <input
                  data-testid="products-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for products…"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-[var(--line)] rounded-md text-sm focus:border-[var(--green)] focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--ink-3)] kbd">Sort by</span>
                <select
                  data-testid="products-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-white border border-[var(--line)] rounded-md px-3 py-2 text-sm focus:border-[var(--green)] focus:outline-none"
                >
                  {SORT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
              {list.map((p, i) => (
                <Reveal key={p.id} delay={(i % 4) * 0.04}>
                  <div className="card-soft overflow-hidden relative h-full flex flex-col">
                    {p.badge && (
                      <span className={`badge-ribbon ${p.badge === "BESTSELLER" ? "badge-bestseller" : "badge-new"}`}>
                        {p.badge}
                      </span>
                    )}
                    <button
                      onClick={() => onWishlist(p)}
                      data-testid={`wishlist-${p.id}`}
                      aria-label="Add to wishlist"
                      className="absolute top-3 right-3 z-[2] w-9 h-9 rounded-full bg-white/95 border border-[var(--line-soft)] flex items-center justify-center hover:bg-[var(--bg-cream)] transition"
                    >
                      <Heart size={16} className="text-[var(--ink-2)]" />
                    </button>
                    <Link
                      to={`/products/${p.id}`}
                      data-testid={`product-view-${p.id}`}
                      className="aspect-[4/5] overflow-hidden bg-white block"
                    >
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </Link>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-serif-display text-base md:text-lg font-semibold text-[var(--ink)] leading-tight line-clamp-2">{p.name}</h3>
                      <div className="text-xs text-[var(--ink-3)] mt-1">{p.weight}</div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {p.tags.slice(0, 2).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-widest border border-[var(--line-soft)] text-[var(--ink-3)] px-2 py-1 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-auto pt-4">
                        <div className="flex items-baseline gap-2">
                          <span className="font-serif-display text-xl font-semibold text-[var(--green)]">₹{priceNum(p.price)}</span>
                          <span className="text-[10px] text-[var(--ink-3)]">{p.per100g}</span>
                        </div>
                        <button
                          onClick={() => onAdd(p)}
                          data-testid={`product-add-${p.id}`}
                          className="btn-primary w-full mt-3 !py-2.5 !text-[0.72rem]"
                        >
                          <ShoppingCart size={14} /> Add to Cart
                        </button>
                        <button
                          onClick={() => setOpen(p)}
                          data-testid={`product-enquire-${p.id}`}
                          className="text-xs uppercase tracking-widest text-[var(--green)] font-semibold mt-2 link-underline"
                        >
                          Enquire / Bulk →
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {!list.length && (
              <div className="card-soft p-14 text-center mt-4">
                <p className="font-serif-display text-2xl text-[var(--green)]">No products match these filters.</p>
                <button onClick={clearAll} className="btn-outline mt-5">Clear filters</button>
              </div>
            )}

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  data-testid="products-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-outline disabled:opacity-60"
                >
                  {loadingMore ? "Loading…" : "Load More Products"}
                </button>
              </div>
            )}

            <div className="mt-10 text-center text-xs text-[var(--ink-3)]">
              Showing {list.length} of {enriched.length} products
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Modal */}
      {open && (
        <div
          data-testid="enquiry-modal"
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setOpen(null)}
        >
          <div className="bg-white rounded-xl max-w-2xl w-full grid grid-cols-1 md:grid-cols-2 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-square md:aspect-auto overflow-hidden bg-[var(--bg-soft)]">
              <img src={open.image} alt={open.name} className="w-full h-full object-cover" />
            </div>
            <form onSubmit={submitEnquiry} className="p-6 md:p-7 flex flex-col">
              <div className="flex justify-between items-start">
                <div>
                  <span className="kbd text-[var(--gold)]">{open.weight} · ₹{priceNum(open.price)}</span>
                  <h3 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight text-[var(--ink)] font-semibold">{open.name}</h3>
                </div>
                <button data-testid="enquiry-close" type="button" onClick={() => setOpen(null)} className="text-[var(--ink-3)] hover:text-[var(--green)]">
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
                For bulk orders, distribution or samples — our pantry team responds within one business day.
              </p>
              <div className="mt-4 space-y-3">
                <input data-testid="enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className="w-full bg-transparent border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
                <input data-testid="enquiry-phone" required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number *" className="w-full bg-transparent border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
                <input data-testid="enquiry-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="w-full bg-transparent border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
                <textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Quantity, city or message (optional)" className="w-full bg-transparent border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
              </div>
              <button data-testid="enquiry-submit" type="submit" disabled={submittingEnquiry} className="btn-primary mt-4 disabled:opacity-60">
                {submittingEnquiry ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="mt-5 pt-5 border-t border-[var(--line-soft)] first:border-t-0 first:mt-3 first:pt-0">
      <div className="font-serif-display text-base font-semibold text-[var(--ink)] mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function CheckRow({ label, checked, onChange, testId }) {
  return (
    <label data-testid={testId} className="flex items-center gap-3 text-sm text-[var(--ink-2)] cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 accent-[var(--green)]" />
      <span>{label}</span>
    </label>
  );
}
